import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
    faArrowRight,
    faChevronRight,
    faClockRotateLeft,
    faFilePdf,
    faHandPointer,
    faMicrophone,
    faSearch,
    faVolumeHigh,
    faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    FILE_TYPE_LABEL,
    JENIS_BUKU_LABEL,
    HighlightedText,
    addRecentBook,
    addRecentQuery,
    getFilterOptions,
    getJenjangLabel,
    getRecentBooks,
    getRecentQueries,
    loadMiniSearch,
    retryMiniSearch,
    resolveBookSlug,
    searchBooks,
    subscribeMiniSearch,
} from '../../../utils/minisearch'
import styles from './Spotlight.module.scss'

const FILE_TYPE_ICON = {
    pdf: faFilePdf,
    audio: faVolumeHigh,
    interactive: faHandPointer,
}

const FILTERS = [
    { key: 'semua', label: 'Semua' },
    { key: 'kurmer', label: 'Kurikulum Merdeka' },
    { key: 'k13', label: 'K13' },
    { key: 'nonteks', label: 'Non-Teks' },
]

// Saran saat palette masih kosong
const POPULAR_KEYWORDS = ['matematika', 'bahasa indonesia', 'bahasa inggris', 'pancasila', 'ipa', 'audio']

const MAX_RESULTS = 8
const MIN_QUERY_LENGTH = 2
const PLACEHOLDER_COVER = '/assets/image/catalog/book-placeholder.jpg'

const getSpeechRecognition = () => {
    if (typeof window === 'undefined') return null
    return window.SpeechRecognition || window.webkitSpeechRecognition || null
}

/**
 * Spotlight-style search palette.
 * - Dibuka lewat trigger di halaman atau shortcut Cmd/Ctrl + K
 * - Data MiniSearch dengan cache auto-sync (lihat utils/minisearch.js)
 * - Filter jenis buku dan tipe file (jenjang tidak dipilih user: aturan BNT/BTP
 *   berbeda-beda, jadi pencocokan jenjang mengandalkan kata kunci dan data internal)
 * - Navigasi keyboard: panah, Enter, Esc, Tab (focus trap)
 * - State pencarian disimpan di URL (?cari=) supaya bisa dibagikan
 */
const Spotlight = ({ open, setOpen, initialQuery = '' }) => {
    const navigate = useNavigate()

    const [query, setQuery] = useState(initialQuery)
    const [jenis, setJenis] = useState('semua')
    const [jenjang, setJenjang] = useState('')
    const [tipe, setTipe] = useState('')
    const [result, setResult] = useState({ items: [], total: 0 })
    const [activeIndex, setActiveIndex] = useState(0)
    const [status, setStatus] = useState('loading')
    const [errorMessage, setErrorMessage] = useState('')
    const [pendingId, setPendingId] = useState(null)
    const [options, setOptions] = useState({ jenjang: [], tipe: [] })
    const [recent, setRecent] = useState({ queries: [], books: [] })
    const [listening, setListening] = useState(false)
    const [voiceHint, setVoiceHint] = useState('')
    const [version, setVersion] = useState(0)

    const inputRef = useRef(null)
    const panelRef = useRef(null)
    const listRef = useRef(null)
    const recognitionRef = useRef(null)
    const startUrlRef = useRef('')
    const seedRef = useRef('')

    const applySnapshot = (snap) => {
        if (snap?.miniSearch) {
            setStatus('ready')
            setErrorMessage('')
            setOptions(getFilterOptions())
        } else {
            setStatus('error')
            setErrorMessage(snap?.error || 'Data pencarian tidak tersedia.')
        }
    }

    // Muat index + ikuti pembaruan data katalog di belakang layar
    useEffect(() => {
        let mounted = true
        const sync = () => {
            loadMiniSearch().then((snap) => {
                if (mounted) applySnapshot(snap)
            })
        }

        sync()
        const unsubscribe = subscribeMiniSearch(() => {
            if (!mounted) return
            setVersion((prev) => prev + 1)
            setOptions(getFilterOptions())
        })

        return () => {
            mounted = false
            unsubscribe()
        }
    }, [])

    // Deep link: /?cari=matematika langsung membuka palette
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const seed = params.get('cari')

        if (params.get('jenis')) setJenis(params.get('jenis'))
        if (params.get('jenjang')) setJenjang(params.get('jenjang'))
        if (params.get('tipe')) setTipe(params.get('tipe'))

        if (seed) {
            seedRef.current = seed
            if (!open) setOpen(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Shortcut global Cmd/Ctrl + K
    useEffect(() => {
        const onGlobalKey = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                if (!open) setOpen(true)
            }
        }

        document.addEventListener('keydown', onGlobalKey)
        return () => document.removeEventListener('keydown', onGlobalKey)
    }, [open, setOpen])

    // Saat dibuka: reset query, fokus input, kunci scroll body
    useEffect(() => {
        if (!open) return

        const previousActive = document.activeElement
        const previousOverflow = document.body.style.overflow

        setQuery(initialQuery || seedRef.current || '')
        seedRef.current = ''
        setVoiceHint('')
        setActiveIndex(0)
        setRecent({ queries: getRecentQueries(), books: getRecentBooks() })
        document.body.style.overflow = 'hidden'

        const timer = setTimeout(() => inputRef.current?.focus(), 50)

        return () => {
            document.body.style.overflow = previousOverflow
            clearTimeout(timer)
            if (previousActive instanceof HTMLElement) previousActive.focus()
        }
    }, [open, initialQuery])

    // Esc global menutup palette (walau fokus pindah dari input)
    useEffect(() => {
        if (!open) return

        const onKey = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                setOpen(false)
            }
        }

        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, setOpen])

    // Hentikan dikte saat palette ditutup
    useEffect(() => {
        if (!open && recognitionRef.current) {
            recognitionRef.current.stop()
            setListening(false)
        }
    }, [open])

    // Sinkronkan state pencarian ke URL (replaceState: tidak menumpuk history)
    useEffect(() => {
        if (!open) return
        startUrlRef.current = `${window.location.pathname}${window.location.search}`
        return () => {
            if (startUrlRef.current) window.history.replaceState(null, '', startUrlRef.current)
        }
    }, [open])

    useEffect(() => {
        if (!open) return

        const base = startUrlRef.current || `${window.location.pathname}${window.location.search}`
        const params = new URLSearchParams(base.includes('?') ? base.slice(base.indexOf('?') + 1) : '')
        const trimmed = query.trim()

        if (trimmed.length >= MIN_QUERY_LENGTH) params.set('cari', trimmed)
        else params.delete('cari')

        if (jenis !== 'semua') params.set('jenis', jenis)
        else params.delete('jenis')

        if (jenjang) params.set('jenjang', jenjang)
        else params.delete('jenjang')

        if (tipe) params.set('tipe', tipe)
        else params.delete('tipe')

        const search = params.toString()
        window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
    }, [open, query, jenis, jenjang, tipe])

    // Jalankan pencarian (debounce ringan)
    useEffect(() => {
        if (!open) return

        const trimmed = query.trim()
        if (status !== 'ready' || trimmed.length < MIN_QUERY_LENGTH) {
            setResult({ items: [], total: 0 })
            setActiveIndex(0)
            return
        }

        const timer = setTimeout(() => {
            setResult(searchBooks(trimmed, { limit: MAX_RESULTS, jenis, jenjang, tipe }))
            setActiveIndex(0)
        }, 150)

        return () => clearTimeout(timer)
    }, [open, status, query, jenis, jenjang, tipe, version])

    // Pastikan item aktif selalu terlihat
    useEffect(() => {
        if (!open) return
        const active = listRef.current?.querySelector(`[data-index="${activeIndex}"]`)
        if (active) active.scrollIntoView({ block: 'nearest' })
    }, [activeIndex, open, result.items.length])

    const close = () => setOpen(false)

    const buildSearchUrl = () => {
        const params = new URLSearchParams()
        const trimmed = query.trim()

        if (trimmed) params.set('q', trimmed)
        if (jenis !== 'semua') params.set('jenis', jenis)
        if (jenjang) params.set('jenjang', jenjang)
        if (tipe) params.set('tipe', tipe)

        return `/pencarian?${params.toString()}`
    }

    /**
     * Buka detail buku lewat slug (format URL lama, mis. /katalog/ilmu-pengetahuan-...).
     * Data MiniSearch hanya punya id, jadi slug di-resolve satu kali lalu di-cache.
     */
    const selectResult = async (item, { newTab = false } = {}) => {
        if (!item || pendingId) return

        setPendingId(item.id)
        addRecentQuery(query)
        addRecentBook(item)

        const slug = item.slug || (await resolveBookSlug(item.id))
        const target = slug ? `/katalog/${slug}` : `/katalog/${item.id}`

        setPendingId(null)
        close()

        if (newTab) window.open(target, '_blank', 'noopener')
        else navigate(target)
    }

    const openAllResults = () => {
        if (query.trim().length < MIN_QUERY_LENGTH) return
        addRecentQuery(query)
        close()
        navigate(buildSearchUrl())
    }

    const toggleVoice = () => {
        const Recognition = getSpeechRecognition()

        // Browser tanpa Web Speech API: tetap kasih tombolnya supaya ketemu,
        // lalu jelaskan alasannya alih-alih tombol menghilang tanpa jejak.
        if (!Recognition) {
            setVoiceHint(
                window.isSecureContext === false
                    ? 'Pencarian suara butuh HTTPS. Buka situs ini lewat https:// lalu coba lagi.'
                    : 'Browser ini belum mendukung pencarian suara. Coba Chrome, Edge, atau Safari terbaru.'
            )
            return
        }

        if (listening) {
            recognitionRef.current?.stop()
            setListening(false)
            return
        }

        const messages = {
            'not-allowed': 'Izin mikrofon ditolak. Aktifkan izin mikrofon untuk situs ini, lalu coba lagi.',
            'service-not-allowed': 'Layanan pengenalan suara diblokir di halaman ini (biasanya butuh HTTPS).',
            'audio-capture': 'Mikrofon tidak terdeteksi. Pastikan perangkat punya mikrofon.',
            network: 'Koneksi bermasalah saat mengenali suara. Coba lagi.',
            'no-speech': 'Tidak ada suara yang terdeteksi. Coba bicara lebih dekat ke mikrofon.',
            aborted: '',
        }

        try {
            const recognition = new Recognition()
            recognition.lang = 'id-ID'
            recognition.interimResults = true
            recognition.maxAlternatives = 1
            recognition.onstart = () => setVoiceHint('')
            recognition.onresult = (event) => {
                const transcript = Array.from(event.results)
                    .map((entry) => entry[0].transcript)
                    .join(' ')
                setQuery(transcript)
                setVoiceHint('')
            }
            recognition.onend = () => setListening(false)
            recognition.onerror = (event) => {
                setListening(false)
                setVoiceHint(messages[event?.error] ?? 'Pencarian suara gagal. Coba lagi.')
            }
            recognitionRef.current = recognition
            setListening(true)
            recognition.start()
        } catch {
            setListening(false)
            setVoiceHint('Pencarian suara gagal dijalankan. Coba muat ulang halaman.')
        }
    }

    const focusablesInPanel = () =>
        Array.from(
            panelRef.current?.querySelectorAll(
                'input, button, select, a[href], [tabindex]:not([tabindex="-1"])'
            ) || []
        ).filter((el) => !el.disabled && el.offsetParent !== null)

    const handlePanelKeyDown = (event) => {
        if (event.key !== 'Tab') return

        const focusables = focusablesInPanel()
        if (focusables.length === 0) return

        const first = focusables[0]
        const last = focusables[focusables.length - 1]

        if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        }
    }

    const handleInputKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActiveIndex((prev) => Math.min(prev + 1, Math.max(result.items.length - 1, 0)))
            return
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActiveIndex((prev) => Math.max(prev - 1, 0))
            return
        }
        if (event.key === 'Home') {
            event.preventDefault()
            setActiveIndex(0)
            return
        }
        if (event.key === 'End') {
            event.preventDefault()
            setActiveIndex(Math.max(result.items.length - 1, 0))
            return
        }
        if (event.key === 'Enter') {
            event.preventDefault()
            if (event.metaKey || event.ctrlKey) {
                selectResult(result.items[activeIndex], { newTab: true })
            } else {
                selectResult(result.items[activeIndex])
            }
        }
    }

    if (!open) return null

    const trimmed = query.trim()
    const isFiltered = jenis !== 'semua' || Boolean(jenjang) || Boolean(tipe)
    const voiceSupported = Boolean(getSpeechRecognition())

    const renderItem = (item, index, isOption = true) => {
        const icon = FILE_TYPE_ICON[item.tipe_file] || faFilePdf
        const fileTypeLabel = FILE_TYPE_LABEL[item.tipe_file] || item.tipe_file
        const jenisLabel = JENIS_BUKU_LABEL[item.jenis_buku] || item.jenis_buku
        const isActive = isOption && index === activeIndex

        return (
            <div
                key={`${isOption ? 'result' : 'recent'}-${item.id}`}
                role={isOption ? 'option' : 'button'}
                aria-selected={isOption ? isActive : undefined}
                data-index={isOption ? index : undefined}
                id={isOption ? `spotlight-item-${index}` : undefined}
                tabIndex={isOption ? undefined : 0}
                className={`${styles.item} ${isActive ? styles.itemActive : ''} ${isOption ? styles.itemAnimated : ''}`}
                aria-busy={pendingId === item.id}
                style={isOption ? { animationDelay: `${Math.min(index, 8) * 18}ms` } : undefined}
                onMouseEnter={isOption ? () => setActiveIndex(index) : undefined}
                onClick={() => selectResult(item)}
                onKeyDown={
                    isOption
                        ? undefined
                        : (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                selectResult(item)
                            }
                        }
                }
            >
                <img
                    src={item.image || PLACEHOLDER_COVER}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className={styles.thumb}
                    onError={(event) => {
                        event.currentTarget.onerror = null
                        event.currentTarget.src = PLACEHOLDER_COVER
                    }}
                />
                <div className={styles.itemBody}>
                    <span className={styles.itemTitle}>
                        <HighlightedText text={item.judul} query={query} />
                    </span>
                    <div className={styles.itemMeta}>
                        <span className={styles.badge}>
                            <FontAwesomeIcon icon={icon} className="me-1" />
                            {fileTypeLabel}
                        </span>
                        <span className={styles.badge}>{jenisLabel}</span>
                        {item.mapel && (
                            <span className={styles.badge}>
                                <HighlightedText text={item.mapel} query={query} />
                            </span>
                        )}
                        {item.kelas && <span className={styles.badge}>Kelas {item.kelas}</span>}
                        {item.jenjang && <span className={styles.badge}>{getJenjangLabel(item.jenjang)}</span>}
                    </div>
                </div>
                {pendingId === item.id ? (
                    <span className={styles.itemSpinner} aria-hidden="true" />
                ) : (
                    <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} />
                )}
            </div>
        )
    }

    const renderEmptyState = () => (
        <div className={styles.emptyState}>
            {recent.queries.length > 0 && (
                <div className={styles.emptyGroup}>
                    <div className={styles.emptyLabel}>
                        <FontAwesomeIcon icon={faClockRotateLeft} className="me-1" />
                        Pencarian terakhir
                    </div>
                    <div className={styles.chipRow}>
                        {recent.queries.map((item) => (
                            <button
                                key={item}
                                type="button"
                                className={styles.chip}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => setQuery(item)}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className={styles.emptyGroup}>
                <div className={styles.emptyLabel}>Kata kunci populer</div>
                <div className={styles.chipRow}>
                    {POPULAR_KEYWORDS.map((keyword) => (
                        <button
                            key={keyword}
                            type="button"
                            className={styles.chip}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => setQuery(keyword)}
                        >
                            {keyword}
                        </button>
                    ))}
                </div>
            </div>

            {recent.books.length > 0 && (
                <div className={styles.emptyGroup}>
                    <div className={styles.emptyLabel}>Terakhir dibuka</div>
                    {recent.books.slice(0, 3).map((item) => renderItem(item, -1, false))}
                </div>
            )}

            <div className={styles.stateHint}>
                Ketik minimal {MIN_QUERY_LENGTH} huruf — judul, mata pelajaran, kelas, atau jenjang.
            </div>
        </div>
    )

    let body

    if (status === 'loading') {
        body = (
            <div className={styles.skeletonList} aria-hidden="true">
                {[0, 1, 2, 3].map((item) => (
                    <div key={item} className={styles.skeletonRow}>
                        <span className={styles.skeletonThumb} />
                        <span className={styles.skeletonLines}>
                            <span className={styles.skeletonLine} />
                            <span className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
                        </span>
                    </div>
                ))}
            </div>
        )
    } else if (status === 'error') {
        body = (
            <div className={styles.state}>
                {errorMessage}
                <button
                    type="button"
                    className={styles.resetFilter}
                    onClick={() => {
                        setStatus('loading')
                        retryMiniSearch().then(applySnapshot)
                    }}
                >
                    Coba lagi
                </button>
            </div>
        )
    } else if (trimmed.length < MIN_QUERY_LENGTH) {
        body = renderEmptyState()
    } else if (result.items.length === 0) {
        body = (
            <div className={styles.state}>
                Tidak ada hasil untuk “{trimmed}”.
                {isFiltered && (
                    <button
                        type="button"
                        className={styles.resetFilter}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                            setJenis('semua')
                            setJenjang('')
                            setTipe('')
                        }}
                    >
                        Hapus semua filter
                    </button>
                )}
                <span className={styles.stateHint}>
                    Coba kata kunci lain, mis. &ldquo;matematika&rdquo; atau &ldquo;kelas 4&rdquo;.
                </span>
            </div>
        )
    } else {
        body = result.items.map((item, index) => renderItem(item, index))
    }

    return createPortal(
        <div className={styles.backdrop} onMouseDown={close} role="presentation">
            <div
                className={styles.panel}
                ref={panelRef}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={handlePanelKeyDown}
                role="dialog"
                aria-modal="true"
                aria-label="Pencarian buku"
            >
                <div className={styles.searchRow}>
                    <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(event) => {
                            setQuery(event.target.value)
                            if (voiceHint) setVoiceHint('')
                        }}
                        onKeyDown={handleInputKeyDown}
                        className={styles.input}
                        placeholder="Cari judul, mapel, kelas, atau jenis buku…"
                        role="combobox"
                        aria-label="Cari buku"
                        aria-expanded={result.items.length > 0}
                        aria-controls="spotlight-results"
                        aria-autocomplete="list"
                        aria-activedescendant={
                            result.items.length > 0 ? `spotlight-item-${activeIndex}` : undefined
                        }
                        autoComplete="off"
                        spellCheck="false"
                    />
                    {query !== '' && (
                        <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => {
                                setQuery('')
                                inputRef.current?.focus()
                            }}
                            aria-label="Hapus kata kunci"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                    <button
                        type="button"
                        className={`${styles.iconButton} ${listening ? styles.iconButtonActive : ''} ${
                            voiceSupported ? '' : styles.iconButtonMuted
                        }`}
                        onClick={toggleVoice}
                        aria-label={listening ? 'Hentikan pencarian suara' : 'Cari dengan suara'}
                        aria-pressed={listening}
                        title={
                            voiceSupported
                                ? 'Cari dengan suara (Bahasa Indonesia)'
                                : 'Pencarian suara tidak didukung di browser ini — klik untuk info'
                        }
                    >
                        <FontAwesomeIcon icon={faMicrophone} />
                    </button>
                    <button type="button" className={styles.escButton} onClick={close}>
                        esc
                    </button>
                </div>

                {voiceHint && (
                    <div className={styles.voiceHint} role="status">
                        <FontAwesomeIcon icon={faMicrophone} className="me-2" />
                        {voiceHint}
                    </div>
                )}
                {listening && !voiceHint && (
                    <div className={`${styles.voiceHint} ${styles.voiceHintActive}`} role="status">
                        <span className={styles.voiceDot} aria-hidden="true" />
                        Mendengarkan… ucapkan judul atau mata pelajaran.
                    </div>
                )}

                <div className={styles.filterBar}>
                    <div className={styles.chips}>
                        {FILTERS.map((filter) => (
                            <button
                                key={filter.key}
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => setJenis(filter.key)}
                                className={`${styles.chip} ${jenis === filter.key ? styles.chipActive : ''}`}
                                aria-pressed={jenis === filter.key}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>

                    <div className={styles.selects}>
                        <select
                            className={styles.select}
                            value={tipe}
                            onChange={(event) => setTipe(event.target.value)}
                            aria-label="Filter tipe buku"
                        >
                            <option value="">Semua tipe</option>
                            {options.tipe.map((item) => (
                                <option key={item} value={item}>
                                    {FILE_TYPE_LABEL[item] || item}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.list} ref={listRef} id="spotlight-results" role="listbox">
                    {body}
                </div>

                <div className={styles.footer}>
                    <div className={styles.hints}>
                        <span><kbd>↑</kbd><kbd>↓</kbd> navigasi</span>
                        <span><kbd>↵</kbd> buka</span>
                        <span><kbd>esc</kbd> tutup</span>
                    </div>

                    <div className={styles.footerRight}>
                        {result.total > 0 && (
                            <span className={styles.count}>
                                {result.items.length < result.total
                                    ? `${result.items.length} dari ${result.total} hasil`
                                    : `${result.total} hasil`}
                            </span>
                        )}
                        {result.total > MAX_RESULTS && (
                            <button type="button" className={styles.seeAll} onClick={openAllResults}>
                                Lihat semua
                                <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default Spotlight
